import type { Draft } from '@relay-towing/ui';
import type { PhotoMeta } from '@/api/truckInspections';

/**
 * A half-done checklist, kept on the phone so a killed app or a dead battery
 * does not send the driver back round the truck. One record per checklist; the
 * key carries the run's clientSubmissionId so a restored run retries the
 * same submit and the server can tell it from a second walk-around.
 */

const PREFIX = 'relay_towing_inspection_run2_';
/**
 * Runs written by the build that keyed them on the truck. They name a truck
 * and no checklist, so there is nothing to restore them into, and a driver
 * caught mid-run by this deploy is one walk-around, not a migration.
 */
const OLD_PREFIX = 'relay_towing_inspection_run_';
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface SavedRun {
  submissionId: string;
  /** The checklist this run is on. */
  templateId: string;
  /** What is being checked: a truck by id, or a piece of equipment by name. */
  subject: {
    truckId?: string;
    equipmentLabel?: string;
    label: string;
  };
  savedAt: number;
  drafts: Record<string, Draft>;
  notes: string;
  signedName: string;
  /**
   * Photos of the subject itself rather than of any one question. Optional
   * because runs saved before this field existed are still restored.
   */
  reportPhotoUrls?: string[];
  /**
   * Where each photo came from, by URL, so a restored run still files the
   * camera shots as camera shots. Optional: a run saved before this field
   * existed has no entries, and a photo with none counts as an upload.
   */
  photoMeta?: Record<string, PhotoMeta>;
}

const keyFor = (templateId: string, submissionId: string) =>
  `${PREFIX}${templateId}_${submissionId}`;

const keysForTemplate = (templateId: string): string[] => {
  const keys: string[] = [];
  const head = `${PREFIX}${templateId}_`;
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(head)) keys.push(key);
  }
  return keys;
};

/** Every run left behind by the truck-keyed build, swept on the way past. */
const dropOldRuns = () => {
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key?.startsWith(OLD_PREFIX) && !key.startsWith(PREFIX)) keys.push(key);
  }
  keys.forEach((key) => localStorage.removeItem(key));
};

export const saveRun = (run: SavedRun) => {
  try {
    localStorage.setItem(
      keyFor(run.templateId, run.submissionId),
      JSON.stringify(run)
    );
  } catch {
    // Storage full or blocked. The answers still live in memory.
  }
};

export const clearRun = (templateId: string, submissionId: string) => {
  try {
    localStorage.removeItem(keyFor(templateId, submissionId));
  } catch {
    // Nothing to clear if it could not be written.
  }
};

/**
 * The newest run for this checklist under 12 hours old, if there is one. Older
 * runs for the checklist are dropped and their ids handed back so their photos
 * can be dropped too.
 */
export const loadRun = (
  templateId: string
): { run: SavedRun | null; stale: string[] } => {
  const stale: string[] = [];
  let run: SavedRun | null = null;
  try {
    dropOldRuns();
    for (const key of keysForTemplate(templateId)) {
      let parsed: SavedRun | null = null;
      try {
        parsed = JSON.parse(localStorage.getItem(key) ?? '') as SavedRun;
      } catch {
        parsed = null;
      }
      if (!parsed?.submissionId || Date.now() - parsed.savedAt >= MAX_AGE_MS) {
        localStorage.removeItem(key);
        if (parsed?.submissionId) stale.push(parsed.submissionId);
        continue;
      }
      if (run && run.savedAt >= parsed.savedAt) {
        localStorage.removeItem(key);
        stale.push(parsed.submissionId);
        continue;
      }
      if (run) {
        localStorage.removeItem(keyFor(templateId, run.submissionId));
        stale.push(run.submissionId);
      }
      run = parsed;
    }
  } catch {
    // Storage blocked. Start clean.
  }
  return { run, stale };
};
